import { useEffect, useState } from 'react';

const STATUS_OPTIONS = ['received', 'acknowledged', 'ready for pickup'];

export default function OrdersTable() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  async function fetchOrders() {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_EXPRESS_URL}/api/admin/orders`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setOrders(data);
    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  }

  async function deleteOrder(orderId) {
    if (!window.confirm('Are you sure you want to delete this order?')) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_EXPRESS_URL}/api/orders/${orderId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!res.ok) throw new Error('Failed to delete order');

      setOrders((prev) => prev.filter((o) => o.id !== orderId));
    } catch (err) {
      alert('Failed to delete order: ' + err.message);
    }
  }

  async function updateStatus(orderId, newStatus) {
    setUpdatingId(orderId);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_EXPRESS_URL}/api/admin/orders/${orderId}/status`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!res.ok) throw new Error('Failed to update');

      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)));
    } catch (err) {
      alert('Failed to update status: ' + err.message);
    }

    setUpdatingId(null);
  }

  function renderTableByStatus(statusLabel, colorClass) {
    const filtered = orders.filter((o) => o.status === statusLabel);
    if (filtered.length === 0) return null;

    return (
      <div className="overflow-x-auto border rounded shadow mt-6 bg-white dark:bg-gray-800">
        <h2 className={`text-xl font-semibold p-4 border-b ${colorClass}`}>
          {statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)} Orders
        </h2>
        <table className="min-w-full table-auto text-sm">
          <thead className="bg-gray-50 dark:bg-gray-700 text-left">
            <tr>
              <th className="p-2 border">Order ID</th>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Email</th>
              <th className="p-2 border">Pickup</th>
              <th className="p-2 border">Choc</th>
              <th className="p-2 border">Van</th>
              <th className="p-2 border">Total</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Notes</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <tr key={o.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="p-2 border">{o.order_id}</td>
                <td className="p-2 border">{o.name}</td>
                <td className="p-2 border">{o.email || '-'}</td>
                <td className="p-2 border">{new Date(o.pickup_time).toLocaleString()}</td>
                <td className="p-2 border">{o.chocolate_qty ?? 0}</td>
                <td className="p-2 border">{o.vanilla_qty ?? 0}</td>
                <td className="p-2 border">${Number(o.total_price).toFixed(2)}</td>
                <td className="p-2 border">
                  <select
                    className="bg-white dark:bg-gray-800 border rounded px-2 py-1"
                    value={o.status}
                    onChange={(e) => updateStatus(o.id, e.target.value)}
                    disabled={updatingId === o.id}
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="p-2 border flex flex-col gap-2">
                  <div>{o.notes || '-'}</div>
                  <button
                    onClick={() => deleteOrder(o.id)}
                    className="text-sm text-red-600 border border-red-500 rounded px-2 py-1 hover:bg-red-100 dark:hover:bg-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {loading ? (
        <p className="p-4">Loading...</p>
      ) : (
        <>
          {renderTableByStatus('received', 'bg-yellow-100 dark:bg-yellow-900')}
          {renderTableByStatus('acknowledged', 'bg-orange-100 dark:bg-orange-900')}
          {renderTableByStatus('ready for pickup', 'bg-green-100 dark:bg-green-900')}
        </>
      )}
    </div>
  );
}
